import { IPromise, IRootScopeService, IScope } from "angular";
import { StateParams, StateService } from "@uirouter/angularjs";
import { ApiTokenType, IApiTokenInfo } from "./models";
import { isApiTokenType } from "./utils";
import { ApiTokensService } from "./services";
import { UserService } from "../users/userService";
import { IIndicator, UIErrorHandlingService } from "@gtmhub/error-handling";
import { AccountResolverService } from "../state/accountResolverService";
import { IAccount } from "@gtmhub/core";
import { IStateInit } from "@gtmhub/core/routing";
import { ICollection } from "@gtmhub/models";
import { getCurrentUserId } from "@gtmhub/users";
import { copyToClipboard } from "@gtmhub/shared/dom";

interface IApiTokensScope extends IScope {
  indicators: {
    apiTokens?: IIndicator;
  };
  apiTokens: IApiTokenInfo[];
  isAdmin: boolean;
  accountId: string;
  revoke(token: IApiTokenInfo): void;
  copyText(token: string): void;
  onPopoverClick(event: JQueryEventObject): void;
  resetIndicator(indicator: keyof IApiTokensScope["indicators"]): void;
}

export class ApiTokensController implements IStateInit {
  public static $inject = ["$scope", "ApiTokensService", "UserService", "UIErrorHandlingService", "AccountResolverService"];

  constructor(
    private $scope: IApiTokensScope,
    private apiTokensService: ApiTokensService,
    private userService: UserService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private accountResolverService: AccountResolverService
  ) {}

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private getAccountId = (account: IAccount): string => {
    return account ? account.id : "";
  };

  private init = (): IPromise<unknown> => {
    this.$scope.indicators = {};
    this.$scope.revoke = this.revoke;
    this.$scope.copyText = this.copyText;
    this.$scope.onPopoverClick = this.onPopoverClick;
    this.$scope.resetIndicator = this.resetIndicator;
    this.$scope.accountId = this.getAccountId(this.accountResolverService.getAccountData());

    this.$scope.$on("tokenIssued", this.getApiTokens);

    return this.userService.getUser(getCurrentUserId()).then((user) => {
      const userRoles = [];
      for (let a = 0; a < user.roles.length; a++) {
        userRoles.push(user.roles[a].name);
      }
      if (userRoles.indexOf("admin") !== -1) {
        this.$scope.isAdmin = true;
      }

      return this.getApiTokens();
    });
  };

  private revoke = (token: IApiTokenInfo) => {
    this.apiTokensService.revoke(token).then(
      () => {
        this.getApiTokens();
      },
      (rejection) => this.uiErrorHandlingService.handleIndicator(rejection, { indicator: "revoking", inline: true })
    );
  };

  private getApiTokens = (): IPromise<unknown> => {
    this.$scope.indicators.apiTokens = { progress: true };

    let getTokensPromise: IPromise<ICollection<IApiTokenInfo>>;
    if (this.$scope.isAdmin) {
      getTokensPromise = this.apiTokensService.getApiTokens();
    } else {
      getTokensPromise = this.apiTokensService.getUserApiTokens(getCurrentUserId());
    }

    return getTokensPromise.then(
      (apiTokens) => {
        delete this.$scope.indicators.apiTokens;
        this.$scope.apiTokens = apiTokens.items;
      },
      (error) => (this.$scope.indicators.apiTokens = { error })
    );
  };

  private copyText = (text: string) => {
    copyToClipboard(text);
  };

  private onPopoverClick(event: JQueryEventObject): void {
    // click outside the popover / click on "Copy" button
    if (!["popover"].some((c) => event.target.classList.contains(c)) || event.target.classList.contains("copy-token")) {
      const allPopups = document.querySelectorAll<HTMLElement>(".popover");
      if (allPopups.length > 0) {
        for (let i = 0; i < allPopups.length; i++) {
          const popup = allPopups[i];
          const popupElement = angular.element(popup);
          // leave only one popover open at a time
          if (popup.previousSibling != event.target && popup.previousSibling != event.target.parentElement) {
            (popupElement.scope().$parent as IScope & { isOpen: boolean }).isOpen = false;
            popup.remove();
          }
        }
      }
    }
  }

  private resetIndicator = (indicator: keyof IApiTokensScope["indicators"]): void => {
    delete this.$scope.indicators[indicator];
  };
}

export interface IIssueTokenScope extends IScope {
  indicators: {
    create?: IIndicator;
  };
  token: IApiTokenInfo;
  isAdmin: boolean;
  applicationTokenTypes;
  issueToken(): void;
  resetIndicator(indicator: keyof IIssueTokenScope["indicators"]): void;
}

export class IssueTokenCtrl {
  public static $inject = ["$scope", "$rootScope", "$state", "ApiTokensService", "UserService", "$stateParams"];

  constructor(
    private $scope: IIssueTokenScope,
    private $rootScope: IRootScopeService,
    private $state: StateService,
    private apiTokensService: ApiTokensService,
    private userService: UserService,
    private $statePartams: StateParams
  ) {
    this.$scope.issueToken = this.issueToken;
    this.$scope.applicationTokenTypes = {
      scim: {
        name: "SCIM",
        key: "scim",
        scope: [
          { "create:users": "Create user" },
          { "read:users": "Read user" },
          { "update:users": "Update user" },
          { "delete:users": "Delete user" },
          { "read:groups": "Read groups" },
        ],
        description: "This API token works with systems supporting SCIM (System for Cross-domain Identity Management).",
      },
    };

    this.init();
  }

  private init = () => {
    this.$scope.indicators = {};
    this.$scope.resetIndicator = this.resetIndicator;

    let tokenType: ApiTokenType = this.$statePartams.tokenType;
    if (!isApiTokenType(tokenType)) {
      tokenType = "impersonation";
    }

    this.$scope.token = {
      user: {
        id: getCurrentUserId(),
        picture: "",
      },
      name: "",
      type: tokenType,
    };

    this.userService.getUser(getCurrentUserId()).then((user) => {
      this.$scope.isAdmin = user.roles.some((r) => r.name.indexOf("admin") !== -1);
    });
  };

  private issueToken = (): void => {
    this.$scope.indicators.create = { progress: true };

    if (this.$scope.token.type === "impersonation") {
      return this.createImpersonationToken();
    }

    if (this.$scope.token.type === "scim") {
      return this.createApplicationToken();
    }
  };

  private createImpersonationToken = () => {
    if (!this.$scope.isAdmin) {
      this.$scope.token.user.id = getCurrentUserId();
    }

    this.handleCreateApiTokenPromise(this.apiTokensService.createApiToken(this.$scope.token));
  };

  private createApplicationToken = () => {
    delete this.$scope.token.user;

    this.handleCreateApiTokenPromise(this.apiTokensService.createApplicationToken(this.$scope.token));
  };

  private handleCreateApiTokenPromise = (promise: IPromise<IApiTokenInfo>) => {
    promise.then(
      () => {
        this.$rootScope.$broadcast("tokenIssued");
        this.$state.go("^.^");
        this.resetIndicator("create");
      },
      (error) => {
        this.$scope.indicators.create = { error };
      }
    );
  };

  private resetIndicator = (indicator: keyof IIssueTokenScope["indicators"]): void => {
    delete this.$scope.indicators[indicator];
  };
}
